// Faixa "Hoje" do Férias e Licenças (redesenho 27/10/2026).
// Substitui os 4 cartões antigos (que vinham de /api/afastamentos/resumo).
// Tudo é calculado aqui com a lista COMPLETA de registros (sem filtro).

import { Box, Paper, Typography, Grid } from '@mui/material';
import {
  RegistroAfast, foraNoDia, contaNoCalendario, somarDias, diasEntre, diaDaSemana,
  SEMANA_LONGA, fmtLonga, fmtSemDM, fmtEmDias, rotuloTipo, juntarNomes,
} from './afastamentosDatas';
import { CARTAO_SX } from './visual';

interface Props {
  todos: RegistroAfast[];
  hoje: string;
  total: number | null;        // equipe de campo (Planner Férias); null = não carregou
  idsEquipe?: number[];
}

const ROTULO_SX = { fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' as const };

function nomesUnicos(regs: RegistroAfast[]): string[] {
  const vistos: Record<number, boolean> = {};
  const nomes: string[] = [];
  regs.forEach(r => {
    if (vistos[r.agente_id]) return;
    vistos[r.agente_id] = true;
    nomes.push(r.agente_nome || `Agente ${r.agente_id}`);
  });
  return nomes;
}

export default function FaixaHoje({ todos, hoje, total, idsEquipe }: Props) {
  // ── Cartão 1: hoje
  const fora = foraNoDia(todos, hoje, idsEquipe);
  const ninguem = fora.length === 0;
  const corHoje = ninguem ? '#065F46' : '#B45309';
  const fundoHoje = ninguem ? '#ecfdf5' : '#fffbeb';
  const bordaHoje = ninguem ? '#a7f3d0' : '#fde68a';

  // ── Cartões 2 e 3: saídas futuras (só o que conta no calendário)
  const futuros = todos
    .filter(r => contaNoCalendario(r) && r.data_inicio > hoje)
    .sort((a, b) => (a.data_inicio < b.data_inicio ? -1 : a.data_inicio > b.data_inicio ? 1 : a.id - b.id));
  const proximaData = futuros.length ? futuros[0].data_inicio : null;
  const proximos = proximaData ? futuros.filter(r => r.data_inicio === proximaData) : [];
  const nomesProx = nomesUnicos(proximos);
  const tiposProx = Array.from(new Set(proximos.map(r => rotuloTipo(r.tipo))));

  const limite = somarDias(hoje, 30);
  const em30 = nomesUnicos(futuros.filter(r => r.data_inicio <= limite));

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Paper elevation={0} sx={{ ...CARTAO_SX, p: 2.5, height: '100%', backgroundColor: fundoHoje, borderColor: bordaHoje }}>
          <Typography sx={{ ...ROTULO_SX, color: corHoje }}>
            Hoje · {SEMANA_LONGA[diaDaSemana(hoje)].toLowerCase()} {fmtLonga(hoje)}
          </Typography>
          <Typography sx={{ fontSize: 34, fontWeight: 800, color: corHoje, lineHeight: 1.15, mt: 1 }}>
            {total !== null ? `${Math.max(0, total - fora.length)} de ${total} em campo` : `${fora.length} fora`}
          </Typography>
          <Typography sx={{ fontSize: 15, color: '#374151', mt: 1 }}>
            {ninguem
              ? 'Ninguém de férias ou licença hoje'
              : `Fora hoje: ${juntarNomes(fora.map(r => r.agente_nome || ''))}`}
          </Typography>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Paper elevation={0} sx={{ ...CARTAO_SX, p: 2.5, height: '100%', backgroundColor: '#fff7ed', borderColor: '#fed7aa' }}>
          <Typography sx={{ ...ROTULO_SX, color: '#7c2d12' }}>Próxima saída</Typography>
          {proximaData ? (
            <>
              <Typography sx={{ fontSize: 30, fontWeight: 800, color: '#7c2d12', lineHeight: 1.15, mt: 1 }}>
                {juntarNomes(nomesProx)}
              </Typography>
              <Typography sx={{ fontSize: 15, color: '#7c2d12', mt: 1 }}>
                Sai {fmtSemDM(proximaData)} · {fmtEmDias(diasEntre(hoje, proximaData))} · {tiposProx.join(' / ')}
              </Typography>
            </>
          ) : (
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#7c2d12', mt: 1 }}>Nenhuma saída marcada</Typography>
          )}
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Paper elevation={0} sx={{ ...CARTAO_SX, p: 2.5, height: '100%' }}>
          <Typography sx={{ ...ROTULO_SX, color: '#1B3A6B' }}>Saem nos próximos 30 dias</Typography>
          <Typography sx={{ fontSize: 34, fontWeight: 800, color: '#1B3A6B', lineHeight: 1.15, mt: 1 }}>
            {em30.length} {em30.length === 1 ? 'agente' : 'agentes'}
          </Typography>
          <Box sx={{ fontSize: 15, color: '#374151', mt: 1 }}>
            {em30.length ? juntarNomes(em30) : 'Ninguém sai nos próximos 30 dias'}
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
}
